import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api, { fmtDate } from "@/lib/api";

function browserNotify(title, body) {
  if ("Notification" in window && Notification.permission === "granted") {
    try { new Notification(title, { body }); } catch { /* unsupported in this context */ }
  }
}

function useNotifications() {
  const [items, setItems] = useState([]);
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      if (seen.current) {
        data.filter((n) => !seen.current.has(n.id)).forEach((n) => {
          toast.info(n.title, { description: n.body });
          browserNotify(n.title, n.body);
        });
      } else if (data.length) {
        toast.info(`${data.length} peringatan belum dibaca`);
      }
      seen.current = new Set(data.map((n) => n.id));
      setItems(data);
    } catch { /* silent poll */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems([]);
  };
  return { items, markAll };
}

export default function NotificationBell() {
  const { items, markAll } = useNotifications();
  const navigate = useNavigate();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-btn">
          <Bell className="h-5 w-5" />
          {items.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white" data-testid="notification-count">
              {items.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-border bg-card p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">Peringatan</p>
          {items.length > 0 && (
            <button onClick={markAll} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="notification-mark-all-btn">
              <CheckCheck className="h-3.5 w-3.5" /> Tanda dibaca
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">Tiada peringatan baharu</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => navigate("/jadual")} className="block w-full border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-accent" data-testid={`notification-item-${n.id}`}>
              <p className="text-sm font-medium">{n.title}</p>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
              <p className="mt-1 font-mono text-[10px] text-muted-foreground">{fmtDate(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
